import axios from 'axios';

/**
 * License Service
 * Handles all API calls related to licenses and subscriptions
 */
export const licenseService = {
  /**
   * Get all available licenses (base + modules)
   * @returns {Promise<{base: License[], modules: License[]}>}
   */
  async getLicenses() {
    try {
      const response = await axios.get('/licenses');
      return response.data;
    } catch (error) {
      console.error('Error fetching licenses:', error);
      throw error;
    }
  },

  /**
   * Get all active subscriptions for current user
   * @returns {Promise<LicenseSubscription[]>}
   */
  async getAllSubscriptions() {
    try {
      const response = await axios.get('/licenses/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }
  },

  /**
   * Start Stripe checkout session
   * @param {string} baseLicenseId - Base license UUID
   * @param {number} quantity - Number of users
   * @param {string[]} moduleIds - Optional module UUIDs
   * @returns {Promise<{url: string}>} Stripe checkout URL
   */
  async startCheckout(baseLicenseId, quantity, moduleIds = []) {
    try {
      const response = await axios.post('/licenses/checkout', {
        baseLicenseId,
        quantity,
        moduleIds,
      });
      return response.data;
    } catch (error) {
      console.error('Error starting checkout:', error);
      throw error;
    }
  },

  /**
   * Open Stripe Customer Portal
   * @returns {Promise<{url: string}>} Portal URL
   */
  async openPortal() {
    try {
      const response = await axios.post('/licenses/portal');
      return response.data;
    } catch (error) {
      console.error('Error opening portal:', error);
      throw error;
    }
  },

  /**
   * Add module to existing subscription
   * @param {string} moduleId - Module UUID
   * @returns {Promise<void>}
   */
  async addModule(moduleId) {
    try {
      await axios.post('/licenses/modules/add', { moduleId });
    } catch (error) {
      console.error('Error adding module:', error);
      throw error;
    }
  },

  /**
   * Update user quantity
   * @param {number} quantity - New quantity
   * @returns {Promise<void>}
   */
  async updateQuantity(quantity) {
    try {
      await axios.patch('/licenses/quantity', { quantity });
    } catch (error) {
      console.error('Error updating quantity:', error);
      throw error;
    }
  },
};
